import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { AnimatedViews, theme } from '@sg/garnish';

import { useCustomerSheet } from '@order/integrations-stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useSavedPaymentMethods } from '../../hooks';
import { AdditionalPaymentMethods } from '../AdditionalPaymentMethods';
import { PaymentMethodEditor } from '../PaymentMethodEditor';
import { SavedPaymentMethod } from '../SavedPaymentMethod';
import { SavedPaymentMethodLoading } from '../SavedPaymentMethodLoading';

export const SavedPaymentMethods = () => {
  const { refetchPaymentMethods, paymentMethods, isFetchingPaymentMethods } =
    useSavedPaymentMethods();

  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  const { formatMessage } = useIntl();
  const addPaymentMethod = formatMessage(messages.addPaymentMethod);

  const { isShowingCustomerSheet, showCustomerSheet, hideCustomerSheet } = useCustomerSheet({
    onSuccess: refetchPaymentMethods,
  });

  const handleClosePaymentMethodEditor = useCallback(() => {
    refetchPaymentMethods();
    hideCustomerSheet();
  }, [refetchPaymentMethods, hideCustomerSheet]);

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  useTrackEffect('payments.viewed');

  const handleAddPaymentMethod = useCallback(() => {
    track('payments.new_payment.pressed');
    void showCustomerSheet(); // notice banners inside
  }, [track, showCustomerSheet]);

  const handleEditPaymentMethod = useCallback(() => {
    track('payments.saved_payment.pressed');
    void showCustomerSheet(); // notice banners inside
  }, [track, showCustomerSheet]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <>
      <View style={styles.container}>
        {isFetchingPaymentMethods ? (
          <>
            <View style={styles.paymentMethods}>
              <SavedPaymentMethodLoading index={0} hasDivider />
              <SavedPaymentMethodLoading index={1} hasDivider />
              <SavedPaymentMethodLoading index={2} />
            </View>

            {isFlexEnabled ? (
              <View style={styles.paymentMethods}>
                <SavedPaymentMethodLoading index={3} />
              </View>
            ) : null}
          </>
        ) : (
          <ScrollView contentContainerStyle={styles.scrollView}>
            <View style={styles.paymentMethods}>
              <AnimatedViews.Reveal>
                {paymentMethods.map((paymentMethod) => (
                  <SavedPaymentMethod
                    key={paymentMethod.id}
                    description={paymentMethod.description}
                    icon={paymentMethod.icon}
                    style={styles.paymentMethod}
                    isDefault={paymentMethod.isDefault}
                    isExpiring={paymentMethod.isExpiring}
                    isExpired={paymentMethod.isExpired}
                    onPress={handleEditPaymentMethod}
                  />
                ))}

                <SavedPaymentMethod
                  icon="IconCreditCard"
                  description={addPaymentMethod}
                  onPress={handleAddPaymentMethod}
                />
              </AnimatedViews.Reveal>
            </View>

            {isFlexEnabled ? <AdditionalPaymentMethods /> : null}
          </ScrollView>
        )}
      </View>

      <PaymentMethodEditor
        visible={isShowingCustomerSheet}
        onClose={handleClosePaymentMethodEditor}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addPaymentMethod: {
    defaultMessage: 'Add credit or debit card',
    description: 'Saved Payment Methods | Add Payment Method',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    maxWidth: 600,
    gap: theme.spacing['4'],
  },
  scrollView: {
    flexGrow: 1,
    gap: theme.spacing['4'],
  },
  paymentMethods: {
    paddingHorizontal: theme.spacing['6'],
    backgroundColor: theme.colors.OPACITY.WHITE.DARKEST,
    borderRadius: theme.radius.large,
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
  },
  paymentMethod: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LIGHT,
  },
});
